import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Mail, Send, Eye, Loader2, Calendar } from "lucide-react";

export default function WeeklyDigestCard() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [previewRes, statusRes] = await Promise.all([
        apiClient.get("/intelligence/digest-preview"),
        apiClient.get("/intelligence/digest-status").catch(() => ({ data: null })),
      ]);
      setData(previewRes.data);
      setStatus(statusRes.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshPreview = async () => {
    setPreviewing(true);
    try {
      const res = await apiClient.get("/intelligence/digest-preview");
      setData(res.data);
      setExpanded(true);
      toast.success("Preview refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    } finally {
      setPreviewing(false);
    }
  };

  const doSend = async (force = false) => {
    setSending(true);
    try {
      const res = await apiClient.post("/intelligence/send-digest", force ? { force: true } : {});
      if (res.data?.dispatched) {
        toast.success(`Digest sent to ${res.data.recipients?.length || 0} recipient(s)`);
        // refresh status after successful send
        const st = await apiClient.get("/intelligence/digest-status").catch(() => ({ data: null }));
        setStatus(st.data);
      } else {
        toast.error(res.data?.reason || "Digest not dispatched");
      }
    } catch (e) {
      const body = e?.response?.data;
      const statusCode = e?.response?.status;
      if (statusCode === 409 && body?.canForce) {
        // Duplicate — offer force
        const when = body.lastSentAt ? new Date(body.lastSentAt).toLocaleString() : "recently";
        if (window.confirm(`Digest already sent ${when} to ${(body.recipients || []).length} recipient(s). Send again anyway?`)) {
          setSending(false);
          return doSend(true);
        }
      } else if (statusCode === 503) {
        toast.error(body?.reason || "Email service not configured");
      } else if (statusCode === 502) {
        toast.error(`Delivery failed: ${body?.reason || "provider error"}`);
      } else {
        toast.error(body?.detail || body?.reason || "Failed to send digest");
      }
    } finally {
      setSending(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send the weekly digest email to the organisation owners now?")) return;
    await doSend(false);
  };

  if (loading) {
    return (
      <Card className="p-4 border-slate-200" data-testid="digest-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading weekly digest…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden) return null;
  if (data.error) return null;

  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;
  const canSend = data.period !== undefined; // preview worked — send button tries and lets backend decide owner-only
  const lastSent = status?.lastSentAt ? new Date(status.lastSentAt) : null;
  const lastSentLabel = lastSent
    ? lastSent.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="weekly-digest-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex-shrink-0 h-9 w-9 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Mail className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 text-sm">Weekly Digest</h3>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {data.periodStart} → {data.periodEnd}
              </span>
              {status?.recentlySent && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-medium" data-testid="digest-already-sent-pill">
                  Already sent today
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-600 flex-wrap">
              <span><span className="font-semibold text-slate-900">{fmtGBP(data.valueCapturedGBP)}</span> captured</span>
              <span className="text-slate-300">·</span>
              <span><span className="font-semibold text-slate-900">{data.actionsCompleted}</span> actions</span>
              <span className="text-slate-300">·</span>
              <span><span className="font-semibold text-slate-900">{data.openOpportunities}</span> open</span>
              {data.avgResponseTimeHuman && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>avg response <span className="font-semibold text-slate-900">{data.avgResponseTimeHuman}</span></span>
                </>
              )}
            </div>
            {lastSentLabel && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="digest-last-sent">
                Last sent {lastSentLabel}{status?.lastSentBy ? ` by ${status.lastSentBy}` : ""}
                {(status?.lastRecipients || []).length ? ` · ${status.lastRecipients.length} recipient${status.lastRecipients.length === 1 ? "" : "s"}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={refreshPreview}
            disabled={previewing}
            className="gap-1"
            data-testid="digest-preview-btn"
          >
            {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            Preview
          </Button>
          <Button
            size="sm"
            onClick={sendNow}
            disabled={sending || !canSend}
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
            data-testid="digest-send-btn"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send Now
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-2" data-testid="digest-expanded">
          {data.topManager && (
            <div className="text-xs text-slate-700">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Top manager</span>
              <span className="font-medium">{data.topManager.name}</span> — {fmtGBP(data.topManager.valueCapturedGBP)} from {data.topManager.actionsCompleted} action(s)
            </div>
          )}
          {data.slowestRegion && (
            <div className="text-xs text-slate-700">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Slowest area</span>
              <span className="font-medium">{data.slowestRegion.name}</span> averaged {data.slowestRegion.avgResponseTimeHuman}
            </div>
          )}
          {data.biggestMissedOpportunity && (
            <div className="text-xs text-slate-700">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Missed</span>
              <span className="font-medium">{data.biggestMissedOpportunity.title}</span> — {fmtGBP(data.biggestMissedOpportunity.estImpactGBP)} left on the table
            </div>
          )}
          {(data.summaryLines || []).map((l, i) => (
            <div key={i} className="text-xs text-slate-500 italic">• {l}</div>
          ))}
        </div>
      )}
    </Card>
  );
}
